import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import SbModal from 'uiCore/SbModal/SbModal';
import SbButton from 'uiCore/SbButton/SbButton';
import SbSelect from 'uiCore/SbSelect/SbSelect';
import { SbSpin } from 'uiCore';
import { getApprovalCandidates } from 'Api/apiService';
import { ASSIGN_APPROVER, getApproverOptions, getAssignApproverState } from 'Utils/sendAndPublish';

const { Text } = Typography;

// The shared Send for Approval page (wireframe: the "Send for Approval" box), opened by every Send for
// Approval: the editor's (nothing is stored until Send is clicked here; Cancel goes back to the editor with the
// edits kept), the main page's and the "Publish without a review?" box's. It lists the HR Managers with the
// approver right the server returns, never the owner; Send stays greyed out until someone is picked, and is
// disabled with a message when nobody is available.
//   open       — shown or not
//   jdTitle    — the JD's title, in bold in the body
//   ownerEmail — the JD's owner, never offered
//   sending    — the send is in flight
//   onCancel() — close (back to where it was opened from)
//   onSend(approverEmail)
const AssignApproverModal = ({ open, jdTitle, ownerEmail, sending, onCancel, onSend }) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSelected(null);
    setLoading(true);
    setLoadFailed(false);
    getApprovalCandidates()
      .then((data) => { if (!cancelled) setOptions(getApproverOptions(data, ownerEmail)); })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setLoadFailed(true);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ownerEmail]);

  const { canSend, message } = getAssignApproverState({ options, selected, loading, loadFailed });

  return (
    <SbModal
      open={open}
      title={ASSIGN_APPROVER.title}
      onCancel={sending ? undefined : onCancel}
      maskClosable={false}
      wrapClassName="jd-confirm-modal"
      destroyOnClose
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={sending} onClick={onCancel}>
          {ASSIGN_APPROVER.cancelLabel}
        </SbButton>,
        <SbButton
          key="send"
          buttonType="primary"
          loading={sending}
          disabled={!canSend || sending}
          onClick={() => onSend(selected)}
        >
          {ASSIGN_APPROVER.sendLabel}
        </SbButton>,
      ]}
    >
      <p>
        {ASSIGN_APPROVER.bodyBefore}
        <b>{jdTitle}</b>
        {ASSIGN_APPROVER.bodyAfter}
      </p>
      <Text strong style={{ display: 'block', marginBottom: 4 }}>{ASSIGN_APPROVER.approverLabel}</Text>
      {loading ? (
        <SbSpin size="small" />
      ) : (
        <SbSelect
          options={options}
          value={selected || undefined}
          onChange={(value) => setSelected(value || null)}
          placeholder={ASSIGN_APPROVER.placeholder}
          disabled={!options.length || sending}
          allowClear
          style={{ width: '100%' }}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      )}
      {message && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }} data-testid="assign-approver-message">
          {message}
        </Text>
      )}
    </SbModal>
  );
};

export default AssignApproverModal;
